import React, { useRef, useState } from 'react';
import { Button, Row } from 'react-bootstrap';
import DataTable from '../BranchProductInventory/DataTable';
import axiosInstance from 'utils/axiosInterceptors';
import { ColumnDef } from '@tanstack/react-table';
import { TDiscount } from 'utils/types';
import CreateDiscountForm from './CreateDiscountForm';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import { PurchaseOrderHandle } from '../PurchaseOrder/CreatePurchaseOrderForm';
import {
  formatDate,
  formatDateFilter,
  formatNumberWithCommas
} from 'utils/formats';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import DatePicker from 'components/base/DatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import BadgeStatus from 'utils/BadgeStatus';

type DiscountFilters = {
  [key: string]: string | undefined;
  discountType: string | undefined;
  status: string | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
};

const Discount = () => {
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const addDiscountFormRef = useRef<PurchaseOrderHandle>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState<DiscountFilters>({
    discountType: queryParams.get('discountType') || undefined,
    status: queryParams.get('status') || undefined,
    startDate: queryParams.get('startDate') || undefined,
    endDate: queryParams.get('endDate') || undefined
  });

  // fetch will be changed and filters must be added
  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    const { data } = await axiosInstance.get(`/api/v1/discounts`, {
      params: {
        page,
        pageSize,
        search,
        orderBy,
        orderByDesc,
        ...filters
      }
    });
    return data;
  };

  const createDiscount = () => {
    setModalProps({
      title: 'Create Discount',
      content: <CreateDiscountForm ref={addDiscountFormRef} />,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitDiscount()
    });
    modalRef.current.show();
  };

  const handleSubmitDiscount = async () => {
    if (addDiscountFormRef.current) {
      try {
        const isSuccess = await addDiscountFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prevFilters => {
      const newFilters = { ...prevFilters, [key]: value };
      const newQueryParams = new URLSearchParams(location.search);

      Object.keys(newFilters).forEach(filterKey => {
        if (newFilters[filterKey]) {
          newQueryParams.set(filterKey, newFilters[filterKey] as string);
        } else {
          newQueryParams.delete(filterKey);
        }
      });

      navigate({ search: newQueryParams.toString() });
      return newFilters;
    });
  };

  const handleDateChange = (key: 'startDate' | 'endDate') => (date: Date[]) => {
    const selectedDate = date[0];
    const formattedDate = formatDateFilter(selectedDate);

    if (filters[key] === formattedDate) {
      handleFilterChange(key, undefined);
    } else {
      handleFilterChange(key, formattedDate);
    }
  };

  const columns: ColumnDef<TDiscount>[] = [
    {
      id: 'discountName',
      header: 'Name',
      accessorKey: 'discountName',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <Link
            to={`/discount-details/${original.discountId}`}
            className="fw-bold fs-9"
          >
            {original.discountName}
          </Link>
        );
      }
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      enableSorting: false
    },
    {
      id: 'discountPercentage',
      header: 'Amount',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const percentage = original.discountPercentage;
        const amount = original.discountAmount;
        return percentage ? (
          <span>{percentage}%</span>
        ) : (
          <span>₱{formatNumberWithCommas(amount)}</span>
        );
      }
    },
    {
      id: 'usageLimit',
      header: 'Limit',
      accessorKey: 'usageLimit',
      enableSorting: false
    },
    {
      id: 'startDate',
      header: 'Start Date',
      accessorKey: 'startDate',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return <span>{formatDate(original.startDate as string)}</span>;
      }
    },
    {
      id: 'endDate',
      header: 'End Date',
      accessorKey: 'endDate',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return <span>{formatDate(original.endDate as string)}</span>;
      }
    },
    {
      id: 'branchName',
      header: 'Branch',
      accessorKey: 'branchName',
      enableSorting: false
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return <BadgeStatus status={original.status} />;
      }
    },
    {
      id: 'isForAllCustomers',
      header: 'All Customers',
      accessorKey: 'isForAllCustomers',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const isForAllCustomers = original.isForAllCustomers;
        return (
          <FontAwesomeIcon
            icon={isForAllCustomers ? faCheckCircle : faTimesCircle}
            style={{ color: isForAllCustomers ? 'green' : 'red' }}
          />
        );
      }
    },
    {
      id: 'isAfterSalesDiscount',
      header: 'After Sales',
      accessorKey: 'isAfterSalesDiscount',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const isAfterSalesDiscount = original.isAfterSalesDiscount;
        return (
          <FontAwesomeIcon
            icon={isAfterSalesDiscount ? faCheckCircle : faTimesCircle}
            style={{ color: isAfterSalesDiscount ? 'green' : 'red' }}
          />
        );
      }
    },
    {
      id: 'isForMembers',
      header: 'For Members',
      accessorKey: 'isForMembers',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const isForMembers = original.isForMembers;
        return (
          <FontAwesomeIcon
            icon={isForMembers ? faCheckCircle : faTimesCircle}
            style={{ color: isForMembers ? 'green' : 'red' }}
          />
        );
      }
    },
    {
      accessorKey: 'numberOfDaysBeforeActive',
      header: 'Days Before Active',
      cell: ({ row }) => row.original.numberOfDaysBeforeActive
    }
  ];

  return (
    <>
      <Row lg="1" className="mb-3">
        <h4>Discount List</h4>
      </Row>
      <DataTable
        queryKey={['discount', isSubmitted, filters]}
        queryFn={fetchData}
        columns={columns}
        rightSideActions={
          <>
            <Form.Select
              value={filters.discountType}
              onChange={e => handleFilterChange('discountType', e.target.value)}
              style={{
                height: '2.4rem',
                width: '15%',
                marginLeft: '-1rem'
              }}
            >
              <option value="">Discount Type</option>
              <option value="Percentage">Percentage</option>
              <option value="FixedAmount">Fixed Amount</option>
            </Form.Select>
            <Form.Select
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              style={{
                height: '2.4rem',
                width: '10%',
                marginLeft: '-1rem'
              }}
            >
              <option value="">Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Form.Select>
            <div
              style={{
                height: '2.4rem',
                width: '15%',
                marginLeft: '-2rem'
              }}
            >
              <DatePicker
                placeholder="Start"
                value={
                  filters.startDate ? new Date(filters.startDate) : undefined
                }
                onChange={handleDateChange('startDate')}
              />
            </div>
            <div
              style={{
                height: '2.4rem',
                width: '15%',
                marginLeft: '-2.5rem'
              }}
            >
              <DatePicker
                placeholder="Start"
                value={filters.endDate ? new Date(filters.endDate) : undefined}
                onChange={handleDateChange('endDate')}
              />
            </div>
            <Button
              variant="primary"
              style={{
                height: '2.4rem',
                marginLeft: '-2.5rem'
              }}
              onClick={createDiscount}
            >
              Create Discount
            </Button>
          </>
        }
      />
      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        modalSize="lg"
      />
    </>
  );
};

export default Discount;
